import { useQuery } from '@tanstack/react-query'
import { createAttackApi, detectionStreams, tacticKey } from '@bloo-team/api-client'
import type { CoverageTotals, Detection, TechniqueDetail } from '@bloo-team/api-client'
import { client } from '@bloo-team/core-shell'
import { isDemoSession } from '@bloo-team/core-shell'

/**
 * Vantage · Coverage — the join, client-side.
 *
 * The wire model (VERIFIED live, docs/inventory/attack-capture.md): a tactic
 * NAME skeleton, a technique-name -> detections map, and a totals endpoint.
 * buildMatrix performs the join the legacy screen did: detections claim
 * their tactic (dirty spellings folded through tacticKey), a technique sits
 * in EVERY tactic its detections claim, and anything claiming a tactic the
 * skeleton does not know folds into 'Unclassified'.
 *
 * analytics only enumerates techniques WITH content, so the uncovered
 * techniques are a COUNT (from totals), never a list — the matrix shows
 * what exists, the strip shows what is missing.
 */

export const liveAttackApi = createAttackApi(client)

export type { CoverageTotals, Detection, TechniqueDetail }

export interface TechniqueCell {
  name: string
  detections: Detection[]
  /** Detections currently active (and not disabled). */
  armed: number
  signals: number
  /** Every stream this cell's content reads — the data-layer link. */
  streams: string[]
}

export interface CoverageTactic {
  name: string
  techniques: TechniqueCell[]
}

export interface CoverageMatrix {
  tactics: CoverageTactic[]
}

/* ------------------------------------------------------------------- build */

export function buildMatrix(
  tacticNames: string[],
  analytics: Record<string, Detection[]>,
): CoverageMatrix {
  // tacticKey -> technique name -> detections
  const byTactic = new Map<string, Map<string, Detection[]>>()
  const skeleton = new Set(tacticNames.map(tacticKey))
  const unclassified =
    tacticNames.find((n) => tacticKey(n) === 'unclassified') ?? 'Unclassified'

  for (const [tech, dets] of Object.entries(analytics)) {
    for (const d of dets) {
      const raw = d.tactic.trim() ? d.tactic : unclassified
      const key = skeleton.has(tacticKey(raw)) ? tacticKey(raw) : tacticKey(unclassified)
      const m = byTactic.get(key) ?? new Map<string, Detection[]>()
      const list = m.get(tech) ?? []
      list.push(d)
      m.set(tech, list)
      byTactic.set(key, m)
    }
  }

  const tactics: CoverageTactic[] = []
  for (const name of tacticNames) {
    const m = byTactic.get(tacticKey(name))
    const techniques: TechniqueCell[] = m
      ? [...m.entries()]
          .map(([tname, dets]) => ({
            name: tname,
            detections: dets,
            armed: dets.filter((d) => d.active && !d.disabled).length,
            signals: dets.reduce((s, d) => s + d.signal_count, 0),
            streams: [...new Set(dets.flatMap(detectionStreams))].sort(),
          }))
          // Firing first, then armed, then by content weight — the eye lands
          // on what matters without scanning the whole column.
          .sort(
            (a, b) =>
              b.signals - a.signals ||
              b.armed - a.armed ||
              b.detections.length - a.detections.length ||
              a.name.localeCompare(b.name),
          )
      : []
    // The skeleton's empty tactics still render — an empty column IS the
    // information; only an empty Unclassified column would be noise.
    if (techniques.length || tacticKey(name) !== 'unclassified') {
      tactics.push({ name, techniques })
    }
  }
  return { tactics }
}

/** 'active' fired signals; 'armed' is watching; 'shelf' is content, inactive. */
export function cellState(t: TechniqueCell): 'shelf' | 'armed' | 'active' {
  if (t.signals > 0) return 'active'
  return t.armed > 0 ? 'armed' : 'shelf'
}

/** Activity tier for the heat overlay, 1..3 against the matrix maximum. */
export function activityTier(signals: number, maxSignals: number): 1 | 2 | 3 {
  if (maxSignals <= 0 || signals <= 0) return 1
  const f = signals / maxSignals
  return f > 0.66 ? 3 : f > 0.33 ? 2 : 1
}

/* ---------------------------------------------------------------- demo mode */

export const DEMO_TACTICS = [
  'Reconnaissance', 'Resource Development', 'Initial Access', 'Execution', 'Persistence',
  'Privilege Escalation', 'Defense Evasion', 'Credential Access', 'Discovery',
  'Lateral Movement', 'Collection', 'Command and Control', 'Exfiltration',
  'Impact', 'Unclassified',
]

function det(
  name: string,
  tactic: string,
  streams: string[],
  opts: { active?: boolean; signals?: number; severity?: string; type?: string } = {},
): Detection {
  return {
    id: name.toLowerCase().replace(/\W+/g, '-'),
    name,
    tactic,
    severity: opts.severity ?? 'Medium',
    active: opts.active ?? false,
    disabled: false,
    native: true,
    is_latest: true,
    type: opts.type ?? 'standard',
    signal_count: opts.signals ?? 0,
    requires_stream: [streams.map((s) => ({ is_data: true, stream: s }))],
  }
}

/**
 * Shaped exactly like the live analytics map, dirt included: a British
 * tactic spelling and a lowercase variant, so the demo exercises the
 * canonicalization the live tenant needs.
 */
export const DEMO_ANALYTICS: Record<string, Detection[]> = {
  'Brute Force': [
    det('Unusual Spike in Failed Login Attempts', 'Credential Access', ['AUTHENTICATION'], { active: true, signals: 57, severity: 'High', type: 'auto-scheduled' }),
    det('Password Spray Across Accounts', 'Credential Access', ['AUTHENTICATION', 'WIN-AUDIT'], { active: true, signals: 6 }),
    det('SSH Brute Force Attempts', 'Credential Access', ['AUDITD'], {}),
  ],
  'Command and Scripting Interpreter': [
    det('Suspicious PowerShell Execution', 'Execution', ['WIN-AUDIT'], { active: true, signals: 41, severity: 'High' }),
    det('Encoded Command Line', 'Execution', ['WIN-AUDIT'], { active: true, signals: 0 }),
  ],
  'Valid Accounts': [
    det('Login outside IN', 'Initial access', ['AUTHENTICATION'], { active: true, signals: 23, severity: 'High' }),
    det('Dormant Account Login', 'Initial Access', ['AUTHENTICATION'], {}),
  ],
  'Remote Services': [
    det('Execute command writing output to local Admin Share', 'Lateral movement', ['WIN-AUDIT'], { active: true, signals: 19 }),
    det('RDP From Unusual Source', 'Lateral Movement', ['FIREWALL'], { active: true, signals: 0 }),
  ],
  'Obfuscated Files or Information': [
    det('Base64 Payload in Command Line', 'Defence Evasion', ['WIN-AUDIT'], { active: true, signals: 12 }),
  ],
  'Application Layer Protocol': [
    det('DNS tunnelling pattern', 'Command and Control', ['DNS'], { active: true, signals: 14, severity: 'High' }),
  ],
  'Protocol Tunneling': [
    det('Long-lived DNS TXT Queries', 'Command and Control', ['DNS'], { active: true, signals: 3 }),
  ],
  'Exfiltration Over C2 Channel': [
    det('Large outbound transfer to rare destination', 'Exfiltration', ['FIREWALL'], { active: true, signals: 2 }),
  ],
  'OS Credential Dumping': [
    det('LSASS memory access', 'Credential Access', ['WIN-AUDIT'], { active: true, signals: 2, severity: 'Critical' }),
  ],
  'Boot or Logon Autostart Execution': [
    det('Autorun registry modification', 'Persistence', ['WIN-AUDIT'], { active: true, signals: 6 }),
  ],
  'Scheduled Task/Job': [
    det('Task Created by Non-Admin', 'Persistence', ['WIN-AUDIT'], { active: true, signals: 0 }),
    det('Cron Entry From Web Process', 'Persistence', ['AUDITD'], {}),
  ],
  'Network Service Discovery': [
    det('Internal Port Sweep', 'Discovery', ['FIREWALL'], { active: true, signals: 8 }),
  ],
  'Data Encrypted for Impact': [
    det('Mass File Rename Burst', 'Impact', ['WIN-AUDIT'], {}),
  ],
  'Abuse Elevation Control Mechanism': [
    det('Suspicious SUDO PASSWD Command Execution', 'Privilege Escalation', ['AUDITD'], {}),
    det('AWS STS AssumeRole Misuse', 'Privilege Escalation', ['CLOUDTRAIL'], {}),
  ],
  'Data from Network Shared Drive': [
    det('Bulk Share Read By Single Host', 'Collection', ['WIN-AUDIT'], {}),
  ],
  'Active Scanning': [
    det('External Scanner Fingerprint', 'Reconnaissance', ['FIREWALL'], { active: true, signals: 4 }),
  ],
  'Exfiltration Over Web Service': [
    det('Upload To Personal Cloud Storage', 'Exfiltration', ['FIREWALL'], {}),
  ],
  // Armed but reads EMAIL-GATEWAY, which the demo daily counts leave DEAD —
  // the degraded/starved demo for the health lens.
  'Internal Spearphishing': [
    det('Suspicious Internal Attachment Blast', 'Lateral movement', ['EMAIL-GATEWAY'], { active: true }),
  ],
  'Legacy Correlation Pack': [
    det('Cross-stream anomaly chain', 'No Such Tactic', ['FIREWALL', 'DNS'], {}),
  ],
}

export const DEMO_TOTALS: CoverageTotals = {
  covered_tactics: 13,
  covered_techniques: 19,
  total_tactics: 14,
  total_techniques: 202,
}

const DEMO_DETAILS: Record<string, Partial<TechniqueDetail>> = {
  T1110: {
    technique_description:
      'Adversaries may use brute force techniques to gain access to accounts when passwords are unknown or when password hashes are obtained.',
    technique_detection:
      'Monitor authentication logs for system and application login failures of valid accounts; high failure volumes may indicate brute force or password spraying.',
    technique_platforms: 'Azure AD, Linux, Windows, macOS, SaaS',
    data_sources: 'Application Log: Application Log Content, User Account: User Account Authentication',
  },
  T1059: {
    technique_description:
      'Adversaries may abuse command and script interpreters to execute commands, scripts, or binaries.',
    technique_platforms: 'Linux, Windows, macOS',
  },
  // A GAP in the demo analytics — exercises the feasibility verdict:
  // AUTHENTICATION flows in the demo, so this reads as a quick win.
  T1621: {
    technique_description:
      'Adversaries may attempt to bypass multi-factor authentication by generating repeated MFA push requests until the user accepts one.',
    technique_detection:
      'Monitor authentication logs for a burst of MFA request generations against a single account, especially outside working hours.',
    technique_platforms: 'Azure AD, SaaS, Windows',
    data_sources: 'User Account: User Account Authentication, Logon Session: Logon Session Creation',
  },
}

function demoDetail(techniqueId: string): TechniqueDetail | null {
  const extra = DEMO_DETAILS[techniqueId]
  if (!extra) return null
  return {
    technique_id: techniqueId,
    technique_name: '',
    technique_description: null,
    technique_detection: null,
    technique_tactics: null,
    technique_platforms: null,
    data_sources: null,
    defenses_bypassed: null,
    sub_technique: '0',
    ...extra,
  }
}

/* ------------------------------------------------------------------- hooks */

function scopeKey(isDemo: boolean) {
  return isDemo ? 'demo' : 'live'
}

export function useAttackTactics(q: { clusterId: string; scopeId: string }) {
  const isDemo = isDemoSession()
  return useQuery<string[]>({
    queryKey: ['attack-tactics', q.clusterId, q.scopeId, scopeKey(isDemo)],
    queryFn: () => (isDemo ? Promise.resolve(DEMO_TACTICS) : liveAttackApi.tactics(q)),
    staleTime: 10 * 60_000,
    retry: 1,
  })
}

export function useAttackAnalytics(q: { clusterId: string; scopeId: string }) {
  const isDemo = isDemoSession()
  return useQuery<Record<string, Detection[]>>({
    queryKey: ['attack-analytics', q.clusterId, q.scopeId, scopeKey(isDemo)],
    queryFn: () => (isDemo ? Promise.resolve(DEMO_ANALYTICS) : liveAttackApi.analytics(q)),
    // ~2.4MB live — fetch once and hold it for the session.
    staleTime: 10 * 60_000,
    gcTime: 30 * 60_000,
    retry: 1,
  })
}

export function useCoverageTotals(q: { clusterId: string; scopeId: string }) {
  const isDemo = isDemoSession()
  return useQuery<CoverageTotals>({
    queryKey: ['attack-totals', q.clusterId, q.scopeId, scopeKey(isDemo)],
    queryFn: () => (isDemo ? Promise.resolve(DEMO_TOTALS) : liveAttackApi.totals(q)),
    staleTime: 10 * 60_000,
    retry: 1,
  })
}

export function useTechniqueDetail(q: {
  clusterId: string
  scopeId: string
  /** ATT&CK T-id; null disables — the drill-in is lazy. */
  techniqueId: string | null
}) {
  const isDemo = isDemoSession()
  return useQuery<TechniqueDetail | null>({
    queryKey: ['attack-technique', q.clusterId, q.scopeId, q.techniqueId, scopeKey(isDemo)],
    queryFn: () =>
      isDemo
        ? Promise.resolve(demoDetail(q.techniqueId as string))
        : liveAttackApi.technique({ ...q, techniqueId: q.techniqueId as string }),
    enabled: q.techniqueId !== null,
    staleTime: 30 * 60_000,
    retry: 1,
  })
}
